var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { html } from "lit-html";
import { useMemo, useRef, useState } from "GlobalShared/haunted/CustomHooks";
import { usePeriodEditorTemplates } from "./period-editor-templates";
import { getQuarter } from "GlobalShared/helpers/dateHelper";
import { VALIDATION_ENDED_STATUSES } from "./PeriodModels";
import { useWorldCountries } from "WebComponents/hooks/WebCustomHooks";
import { currentLocale } from "../../../globals";
import { PeriodVersionStatus } from "WebServices/APIs/self/models/V1/Cesop/Periods/PeriodVersion";
import i18next from "i18next";
import { useEffect } from "haunted";
export const usePeriodEditor = ({ refresh, periodManager }) => {
  const close = () => __async(void 0, null, function* () {
    setIsOpen(false);
    setPeriod(void 0);
    window.clearTimeout(timeoutHandlerRef.current);
    yield refresh();
  });
  const open = (periodId) => __async(void 0, null, function* () {
    const response = yield periodManager.get(periodId, true);
    setPeriod(response);
    setIsOpen(true);
  });
  const reload = () => __async(void 0, null, function* () {
    if (period) {
      const response = yield periodManager.get(period.Id, false);
      setPeriod(response);
    }
  });
  const isSourceFileProcessing = () => {
    var _a;
    return [...(_a = period == null ? void 0 : period.CurrentVersion.SourceFiles) != null ? _a : [], period.CurrentVersion.Payees].filter((x) => x).some((sf) => !VALIDATION_ENDED_STATUSES.includes(sf.Status) && sf.IsExpired !== true);
  };
  const isDataGenerationInProgress = () => (period == null ? void 0 : period.CurrentVersion.Status) === PeriodVersionStatus.DocumentGenerationInProgress;
  const isCorrectionGenerationInProgress = () => (period == null ? void 0 : period.CurrentVersion.Status) === PeriodVersionStatus.CorrectionNeeded;
  const isPollingNeeded = () => {
    if (!period)
      return false;
    return isSourceFileProcessing() || isDataGenerationInProgress() || isCorrectionGenerationInProgress();
  };
  const finalizeUpload = () => __async(void 0, null, function* () {
    yield periodManager.finalizeUpload(period);
    yield reload();
    yield refresh();
  });
  const [isOpen, setIsOpen] = useState(false);
  const [period, setPeriod] = useState();
  const timeoutHandlerRef = useRef(void 0);
  const countries = useWorldCountries(currentLocale);
  const {
    viewFilesModalTemplate,
    dataUploadedTemplate,
    documentGenerationTemplate,
    documentsGeneratedTemplate,
    submissionInProgressTemplate,
    preparingCorrectionTemplate,
    submittedTemplate,
    uploadTemplate,
    correctionNeededTemplate
  } = usePeriodEditorTemplates({
    period,
    periodManager,
    reload,
    refresh,
    finalizeUpload
  });
  const label = useMemo(() => {
    return period ? `Q${getQuarter(period.StartDate)} ${period.StartDate.getFullYear()} | ${countries.find((country) => country.Code === period.ReportingCountry).Name} | ${period.Psp.Name.Value} |${i18next.t("Registration id")}: ${period.RegistrationIdentifier ? period.RegistrationIdentifier : i18next.t("N/A")}` : "";
  }, [period]);
  useEffect(() => {
    if (isPollingNeeded()) {
      timeoutHandlerRef.current = window.setTimeout(() => {
        reload();
      }, 2e3);
      return () => window.clearTimeout(timeoutHandlerRef.current);
    }
    return void 0;
  }, [period]);
  const periodVersionsTemplate = () => period == null ? void 0 : period.Versions.sort((a, b) => a.Version > b.Version ? -1 : 1).map((version) => {
    switch (version.Status) {
      case PeriodVersionStatus.DataUploaded:
        return dataUploadedTemplate(version);
      case PeriodVersionStatus.DocumentGenerationInProgress:
        return documentGenerationTemplate(version);
      case PeriodVersionStatus.ReadyToSubmit:
        return documentsGeneratedTemplate(version);
      case PeriodVersionStatus.SubmittedToTa:
        return submissionInProgressTemplate(version);
      case PeriodVersionStatus.AcceptedByCesop:
        return submittedTemplate(version);
      case PeriodVersionStatus.SubmissionInProgress:
        return submissionInProgressTemplate(version);
      case PeriodVersionStatus.PreparingCorrection:
        return preparingCorrectionTemplate(version);
      case PeriodVersionStatus.AwaitingData:
        return uploadTemplate(version);
      default:
        return correctionNeededTemplate(version);
    }
  });
  const template = period ? html`
				<dc-modal .overflowW=${false} .visible=${isOpen} @close=${close} .header=${label} .content=${periodVersionsTemplate()}> </dc-modal>
				${viewFilesModalTemplate(label)}
		  ` : "";
  return { open, template };
};
